<!--
  The dropdown every Insights filter uses. Click the field to open it. A search box sits at the
  top of the list, so you can see what you've typed, and the list narrows as you type.

  Why not Kendo's MultiSelect / DropDownList: their "filterable" mode types straight into the
  field with no visible search box. Inside a <label> they also sometimes swallowed the click or
  opened late, because the label forwarded the click a second time and toggled them shut again.
  This is a plain button and a list, so a click always opens it straight away.

  multiple (default)  v-model is an array of the picked items (the item objects themselves, as
                      the filter bars and the CGS payload expect). Empty means All.
  :multiple="false"   v-model is one item. Used for Time frame, Week starting and the like.
  items               strings, or objects shown by `textField` and told apart by `keyField`.
-->
<template>
  <div ref="root" class="ins-pick" :class="{ 'is-open': open, 'is-disabled': disabled }" @keydown.esc.stop="close(true)">
    <button ref="trigger"
            type="button"
            class="ins-pick__field"
            :aria-label="label ? `${label}: ${summary}` : summary"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :disabled="disabled"
            @click="toggle">
      <span class="ins-pick__text" :class="{ 'is-placeholder': isAll }">{{ summary }}</span>
      <i class="fa-solid fa-chevron-down ins-pick__chev" aria-hidden="true"></i>
    </button>

    <div v-if="open" class="ins-pick__pop" :class="{ 'is-right': alignRight }" role="dialog" :aria-label="label || 'Options'">
      <div class="ins-pick__search">
        <i class="fa-light fa-magnifying-glass" aria-hidden="true"></i>
        <input ref="search"
               v-model="query"
               type="text"
               :placeholder="`Search ${label ? label.toLowerCase() : ''}`.trim()"
               :aria-label="`Search ${label || 'options'}`"
               autocomplete="off"
               @keydown.down.prevent="move(1)"
               @keydown.up.prevent="move(-1)"
               @keydown.enter.prevent="pickActive" />
        <button v-if="query" type="button" class="ins-pick__clear-q" aria-label="Clear search" @click="query = ''; $refs.search.focus()">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      <ul class="ins-pick__list" role="listbox" :aria-multiselectable="multiple ? 'true' : 'false'">
        <li v-if="multiple && !query"
            role="option"
            :aria-selected="isAll ? 'true' : 'false'"
            :class="['ins-pick__opt', { 'is-on': isAll, 'is-active': active === -1 }]"
            @mousedown.prevent
            @click="clear">
          <span class="ins-pick__box" aria-hidden="true"><i v-if="isAll" class="fa-solid fa-check"></i></span>All
        </li>
        <li v-for="(it, i) in visible"
            :key="keyOf(it)"
            role="option"
            :aria-selected="isOn(it) ? 'true' : 'false'"
            :class="['ins-pick__opt', { 'is-on': isOn(it), 'is-active': active === i }]"
            @mousedown.prevent
            @mouseenter="active = i"
            @click="pick(it)">
          <span v-if="multiple" class="ins-pick__box" aria-hidden="true"><i v-if="isOn(it)" class="fa-solid fa-check"></i></span>
          <span class="ins-pick__label">{{ textOf(it) }}</span>
        </li>
        <li v-if="!visible.length" class="ins-pick__none">No matches for "{{ query }}"</li>
        <li v-if="hiddenCount" class="ins-pick__none">{{ hiddenCount }} more. Keep typing to narrow the list.</li>
      </ul>

      <div v-if="multiple" class="ins-pick__foot">
        <span>{{ isAll ? 'All' : `${selected.length} selected` }}</span>
        <button type="button" class="ins-pick__done" @click="close(true)">Done</button>
      </div>
    </div>
  </div>
</template>

<script>
// Long lists (loan officers) render this many rows at most. Typing narrows the rest.
const MAX_ROWS = 300

export default {
  name: 'PickList',
  props: {
    modelValue: { type: [Array, String, Number, Object], default: () => [] },
    items: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: true },
    textField: { type: String, default: '' },
    keyField: { type: String, default: '' },
    label: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return { open: false, query: '', active: 0, alignRight: false }
  },
  computed: {
    selected() {
      if (!this.multiple) return this.modelValue === null || this.modelValue === undefined || this.modelValue === '' ? [] : [this.modelValue]
      return Array.isArray(this.modelValue) ? this.modelValue : []
    },
    selectedKeys() { return new Set(this.selected.map(this.keyOf)) },
    isAll() { return this.multiple && this.selected.length === 0 },
    summary() {
      if (!this.multiple) return this.selected.length ? this.textOf(this.selected[0]) : 'Select…'
      if (this.isAll) return 'All'
      if (this.selected.length === 1) return this.textOf(this.selected[0])
      return `${this.selected.length} selected`
    },
    matches() {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.items
      return this.items.filter(it => this.textOf(it).toLowerCase().includes(q))
    },
    visible() { return this.matches.slice(0, MAX_ROWS) },
    hiddenCount() { return Math.max(0, this.matches.length - MAX_ROWS) }
  },
  watch: {
    query() { this.active = 0 }
  },
  beforeUnmount() { this.unlisten() },
  methods: {
    textOf(it) {
      if (it === null || it === undefined) return ''
      if (typeof it !== 'object') return String(it)
      return String(it[this.textField] ?? it.display ?? it.displayName ?? it.value ?? '')
    },
    keyOf(it) {
      if (it === null || it === undefined || typeof it !== 'object') return String(it)
      const k = this.keyField ? it[this.keyField] : (it.value ?? it.email ?? it.encompassId)
      // Some LO rows share an empty email; fall back to the name so they stay distinct.
      return String(k === '' || k === undefined || k === null ? this.textOf(it) : k)
    },
    isOn(it) { return this.selectedKeys.has(this.keyOf(it)) },
    toggle() { this.open ? this.close(false) : this.openPop() },
    openPop() {
      if (this.disabled) return
      this.query = ''
      this.active = 0
      // Open leftwards when the field sits near the right edge, so the list stays on screen.
      const r = this.$refs.root.getBoundingClientRect()
      this.alignRight = r.left + 280 > window.innerWidth
      this.open = true
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
      document.addEventListener('mousedown', this.onOutside, true)
    },
    close(refocus) {
      if (!this.open) return
      this.open = false
      this.unlisten()
      if (refocus) this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus())
    },
    unlisten() { document.removeEventListener('mousedown', this.onOutside, true) },
    onOutside(e) { if (this.$refs.root && !this.$refs.root.contains(e.target)) this.close(false) },
    emit(v) {
      this.$emit('update:modelValue', v)
      this.$emit('change', v)
    },
    pick(it) {
      if (!this.multiple) {
        this.emit(it)
        this.close(true)
        return
      }
      const k = this.keyOf(it)
      const next = this.isOn(it) ? this.selected.filter(x => this.keyOf(x) !== k) : [...this.selected, it]
      this.emit(next)
    },
    clear() { this.emit([]) },
    move(d) {
      const n = this.visible.length
      if (!n) return
      this.active = (this.active + d + n) % n
      this.$nextTick(() => {
        const el = this.$el.querySelector('.ins-pick__opt.is-active')
        if (el) el.scrollIntoView({ block: 'nearest' })
      })
    },
    pickActive() {
      const it = this.visible[this.active]
      if (it !== undefined) this.pick(it)
    }
  }
}
</script>
